import { SelectOption } from 'components/ui/select'

export type BenefitItem = string | [string, number | string]

interface Benefits {
  tier1: BenefitItem[]
  tier2: BenefitItem[]
  tier3: BenefitItem[]
}

interface PriceValue {
  currency: string
  value: string
}

export interface CardItem {
  tier: string
  title: string
  description: readonly string[]
  isBest?: boolean
  benefits: BenefitItem[]
  buttonText: string
  price: PriceValue
}

interface Card {
  billingCycle: string
  prefix?: string
  items: readonly CardItem[]
}

export interface Cards {
  monthly: Card
  lifetime: Card
}

export type TableValue = string | boolean | [string, number | string]

interface TableItem {
  name: string
  tooltip?: string
  subtitle?: string
  values: TableValue[]
}

interface TableSection {
  name: string | [string, number | string]
  items?: TableItem[]
  values?: TableValue[]
  isNew?: boolean
}

interface MainInfoItem {
  isBest?: boolean
  tier: string
  title: string
  description: readonly string[]
}

interface MainInfo {
  [key: string]: MainInfoItem
}

const MAIN_INFO: MainInfo = {
  tier1: {
    isBest: true,
    tier: 'tier1',
    title: 'individual',
    description: ['managing_1'],
  },
  tier2: {
    tier: 'tier2',
    title: 'business',
    description: ['managing_7'],
  },
  tier3: {
    tier: 'tier3',
    title: 'enterprise',
    description: ['managing_8_plus'],
  },
}

export const BENEFITS: Benefits = {
  tier1: [
    'one_user',
    ['additional_project', 1],
    ['n_searches_day', 150],
    ['competitors_per_project', 5],
    ['scans_per_project', '1,000'],
    ['keywords_per_project', 125],
    ['locations_per_project', 20],
  ],
  tier2: [
    ['n_users', 2],
    ['n_projects', 7],
    ['n_searches_day', 300],
    ['competitors_per_project', 10],
    ['scans_per_project', '5,000'],
    ['keywords_per_project', 150],
    'unlimited_locations_per_project',
  ],
  tier3: [
    ['n_users', 5],
    ['n_projects', 15],
    ['n_searches_day', 900],
    ['competitors_per_project', 15],
    ['scans_per_project', '10,000'],
    ['keywords_per_project', 300],
    'unlimited_locations_per_project',
  ],
}

export const CARDS_MONTHLY: Card = {
  billingCycle: '',
  prefix: 'slash_month',
  items: [
    {
      ...MAIN_INFO.tier1,
      benefits: BENEFITS.tier1,
      buttonText: 'select_individual',
      price: {
        value: '',
        currency: '',
      },
    },
    {
      ...MAIN_INFO.tier2,
      benefits: BENEFITS.tier2,
      buttonText: 'select_business',
      price: {
        value: '',
        currency: '',
      },
    },
    {
      ...MAIN_INFO.tier3,
      benefits: BENEFITS.tier3,
      buttonText: 'select_enterprise',
      price: {
        value: '',
        currency: '',
      },
    },
  ],
}

export const CARDS_LIFETIME: Card = {
  billingCycle: 'lifetime',
  prefix: 'no_monthly_fees',
  items: [
    {
      ...MAIN_INFO.tier1,
      benefits: BENEFITS.tier1,
      buttonText: 'select_individual',
      price: {
        value: '',
        currency: '',
      },
    },
    {
      ...MAIN_INFO.tier2,
      benefits: BENEFITS.tier2,
      buttonText: 'select_business',
      price: {
        value: '',
        currency: '',
      },
    },
    {
      ...MAIN_INFO.tier3,
      benefits: BENEFITS.tier3,
      buttonText: 'select_enterprise',
      price: {
        value: '',
        currency: '',
      },
    },
  ],
}

export const CARDS: Cards = {
  monthly: CARDS_MONTHLY,
  lifetime: CARDS_LIFETIME,
}

export const title = 'upgrade_and_win'

export const TABS: SelectOption[] = [
  {
    name: 'monthly',
    id: 1,
    value: 'monthly',
  },
  {
    name: 'lifetime',
    id: 2,
    value: 'lifetime',
    badge: {
      value: 'save_ninety',
      variant: 'blue500',
    },
  },
]

export const TABLE_CONTENT = (
  translate: (key: string, ...args: (string | number)[]) => string,
): TableSection[] => [
  {
    name: 'subheading_training',
    items: [
      {
        name: 'new_feature_course',
        tooltip: 'new_feature_course_descrip',
        values: [true, true, true],
      },
      {
        name: 'new_feature_templates',
        tooltip: 'new_feature_templates_descrip',
        values: [true, true, true],
      },
      {
        name: 'new_feature_call',
        tooltip: 'new_feature_call_descrip',
        values: [true, true, true],
      },
      {
        name: 'new_feature_support',
        tooltip: 'new_feature_support_descrip',
        values: [true, true, true],
      },
    ],
  },
  {
    name: 'reports_per_day',
    values: ['150', '300', '900'],
  },
  {
    name: 'projects',
    values: ['1', '7', '15'],
  },
  {
    name: 'extension_search_limits',
    values: ['150', '300', '900'],
  },
  {
    name: 'rank_tracking',
    items: [
      {
        name: 'tracked_keywords',
        tooltip: 'tracked_keywords_description',
        values: [
          ['kw_per_project', 125],
          ['kw_per_project', 150],
          ['kw_per_project', 300],
        ],
      },
      {
        name: 'update_frequency',
        tooltip: 'update_frequency_description',
        values: ['daily', 'daily', 'daily'],
      },
      {
        name: 'mobile_rank_tracking',
        tooltip: 'mobile_rank_tracking_description',
        values: [true, true, true],
      },
      {
        name: 'number_of_locations',
        tooltip: 'number_of_locations_description',
        values: ['20', 'unlimited', 'unlimited'],
      },
      {
        name: 'data_reporting',
        tooltip: 'data_reporting_description',
        values: [true, true, true],
      },
    ],
  },
  {
    name: 'site_audit',
    items: [
      {
        name: 'page_crawled_per_report',
        tooltip: 'page_crawled_per_report_description',
        values: ['1,000', '5,000', '10,000'],
      },
      {
        name: 'crawling_frequency',
        tooltip: 'crawling_frequency_description',
        values: ['weekly', 'weekly', 'weekly'],
      },
    ],
  },
  {
    name: 'keyword_research',
    items: [
      {
        name: 'historical_data',
        tooltip: 'keyword_historical_data_description',
        values: [true, true, true],
      },
      {
        name: 'keyword_suggestions',
        tooltip: 'keyword_suggestions_description',
        values: ['20,000', '50,000', '100,000'],
      },
      {
        name: 'content_ideas',
        tooltip: 'content_ideas_meta_description',
        values: ['2,000', '5,000', '10,000'],
      },
      {
        name: 'data_exporting_per_exporting',
        tooltip: 'data_exporting_per_exporting_description',
        values: [
          ['n_rows', '2,000'],
          ['n_rows', '5,000'],
          ['n_rows', '10,000'],
        ],
      },
      {
        name: 'filtering',
        tooltip: 'filtering_description',
        values: [true, true, true],
      },
    ],
  },
  {
    name: 'competitive_analysis',
    items: [
      {
        name: 'menu_similar_websites',
        tooltip: 'competitor_similar_websites_description',
        values: ['20', '20', '20'],
      },
      {
        name: 'tracked_competitors',
        tooltip: 'competitor_tracked_competitors_description',
        values: ['5', '10', '15'],
      },
      {
        name: 'historical_data',
        tooltip: 'keyword_historical_data_description',
        values: [true, true, true],
      },
      {
        name: 'top_pages',
        tooltip: 'competitor_top_pages_description',
        values: ['2,000', '5,000', '10,000'],
      },
      {
        name: 'top_keywords',
        tooltip: 'competitor_top_keywords_description',
        values: ['20,000', '50,000', '100,000'],
      },
      {
        name: 'data_exporting_per_exporting',
        tooltip: 'data_exporting_per_exporting_description',
        values: [
          ['n_rows', '2,000'],
          ['n_rows', '5,000'],
          ['n_rows', '10,000'],
        ],
      },
      {
        name: 'filtering',
        tooltip: 'filtering_description',
        values: [true, true, true],
      },
    ],
  },
  {
    name: 'backlinks',
    items: [
      {
        name: 'link_history',
        tooltip: 'link_history_description',
        values: [
          ['n_years', 3],
          ['n_years', 3],
          ['n_years', 3],
        ],
      },
      {
        name: 'backlinks_rows',
        tooltip: 'backlinks_rows_description',
        values: ['2,000', '5,000', '10,000'],
      },
      {
        name: 'new_and_lost_links',
        tooltip: 'new_and_lost_links_description',
        values: [
          ['n_days', 30],
          ['n_days', 30],
          ['n_days', 30],
        ],
      },
      {
        name: 'data_exporting_per_exporting',
        tooltip: 'data_exporting_per_exporting_description',
        values: [
          ['n_rows', '2,000'],
          ['n_rows', '5,000'],
          ['n_rows', '10,000'],
        ],
      },
      {
        name: 'filtering',
        tooltip: 'filtering_description',
        values: [true, true, true],
      },
    ],
  },
  {
    name: ['add_ons_pricing', 5],
    isNew: true,
    items: [
      {
        name: translate('add_on_searches', 500),
        tooltip: 'add_on_searches_description',
        values: [true, true, true],
      },
      {
        name: 'add_on_domains',
        tooltip: translate('add_on_domains_description', 125, 3),
        subtitle: translate('add_on_domains_subheading', 125, 3),
        values: [true, true, true],
      },
      {
        name: translate('add_on_tracked_kw', 250),
        tooltip: translate('add_on_tracked_kw_description', 250),
        values: [true, true, true],
      },
      {
        name: 'add_on_competitor',
        tooltip: 'add_on_competitor_description',
        values: [true, true, true],
      },
      {
        name: 'add_on_users',
        tooltip: 'add_on_users_description',
        values: [true, true, true],
      },
    ],
  },
]
